/**
 * Admin Invitations Page
 * 
 * FIXED: Removed complex Radix components from dialog form to prevent
 * "Maximum update depth exceeded" crash caused by ref churn during
 * rapid state updates.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { normalizeError } from "../../lib/normalizeError";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Mail,
  Plus,
  RefreshCw,
  Trash2,
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  AlertTriangle,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const ROLES = [
  { value: "admin", label: "Admin", description: "Full access to all features" },
  { value: "purchasing", label: "Purchasing", description: "Manage orders and suppliers" },
  { value: "receiving", label: "Receiving", description: "Process incoming inventory" },
  { value: "scanner", label: "Scanner", description: "Scan station access only" },
  { value: "viewer", label: "Viewer", description: "Read-only access" }
];

const STATUS_COLORS = {
  PENDING: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  ACCEPTED: "bg-green-500/20 text-green-400 border-green-500/30",
  EXPIRED: "bg-muted text-muted-foreground border-border",
  REVOKED: "bg-red-500/20 text-red-400 border-red-500/30"
};

const EMAIL_STATUS_COLORS = {
  sent: "bg-green-500/20 text-green-400 border-green-500/30",
  failed: "bg-red-500/20 text-red-400 border-red-500/30",
  not_sent: "bg-muted text-muted-foreground border-border"
};

const EMAIL_STATUS_LABELS = {
  sent: "Sent",
  failed: "Failed",
  not_sent: "Not Sent"
};

const STATUS_ICONS = {
  PENDING: Clock,
  ACCEPTED: CheckCircle,
  EXPIRED: AlertTriangle,
  REVOKED: XCircle
};

// Simple modal component using native elements (no Radix Dialog)
function SimpleModal({ open, onClose, title, description, children }) {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80"
        onClick={onClose}
      />
      {/* Content */}
      <div className="relative z-50 w-full max-w-md mx-4 bg-background border rounded-lg shadow-lg p-6">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
        >
          <X className="h-4 w-4" />
        </button>
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          {description && (
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        {/* Body */}
        {children}
      </div>
    </div>
  );
}

export default function Invitations() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  
  // Form state - separate primitives to avoid object reference issues
  const [formEmail, setFormEmail] = useState("");
  const [formRoles, setFormRoles] = useState(["viewer"]);
  const [formExpires, setFormExpires] = useState("7");

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem("kanban_token")}`
  }), []);

  // Format date
  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  }, []);

  // Fetch invitations
  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/admin/invitations`, {
        withCredentials: true,
        params
      });
      setInvitations(response.data || []);
    } catch (err) {
      toast.error("Failed to load invitations");
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, getAuthHeaders]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormEmail("");
    setFormRoles(["viewer"]);
    setFormExpires("7");
  }, []);

  // Open modal
  const openModal = useCallback(() => {
    resetForm();
    setModalOpen(true);
  }, [resetForm]);

  // Close modal
  const closeModal = useCallback(() => {
    setModalOpen(false);
    resetForm();
  }, [resetForm]);

  // Toggle role
  const toggleRole = useCallback((role) => {
    setFormRoles(prev => {
      if (prev.includes(role)) {
        const filtered = prev.filter(r => r !== role);
        return filtered.length > 0 ? filtered : ["viewer"];
      }
      return [...prev, role];
    });
  }, []);

  // Create invitation
  const handleCreate = useCallback(async (e) => {
    e.preventDefault();
    
    if (!formEmail) {
      toast.error("Email is required");
      return;
    }
    
    setCreating(true);
    
    try {
      const response = await axios.post(
        `${API}/admin/invitations`,
        {
          email: formEmail,
          roles: formRoles,
          expires_in_days: parseInt(formExpires, 10)
        },
        { withCredentials: true }
      );
      
      const result = response.data;
      const emailError = result.email_error ? ` (Email not sent: ${result.email_error})` : "";
      
      if (result.email_sent) {
        toast.success(`Invitation sent to ${formEmail}`);
      } else {
        toast.success(`Invitation created for ${formEmail}${emailError}`);
      }
      
      // Copy URL to clipboard (silent fail)
      if (result.invite_url) {
        navigator.clipboard.writeText(result.invite_url).catch(() => {});
      }
      
      closeModal();
      fetchInvitations();
    } catch (err) {
      toast.error(normalizeError(err.response?.data) || "Failed to create invitation");
    } finally {
      setCreating(false);
    }
  }, [formEmail, formRoles, formExpires, getAuthHeaders, closeModal, fetchInvitations]);

  // Resend invitation
  const handleResend = useCallback(async (inviteId) => {
    setActionLoading(prev => ({ ...prev, [inviteId]: "resend" }));
    try {
      const response = await axios.post(
        `${API}/admin/invitations/${inviteId}/resend`,
        {},
        { withCredentials: true }
      );
      
      const emailError = response.data.email_error ? ` (Email not sent: ${response.data.email_error})` : "";
      
      if (response.data.email_sent) {
        toast.success("Invitation email resent");
      } else {
        toast.info(`New invite link generated${emailError}`);
      }
      
      if (response.data.invite_url) {
        navigator.clipboard.writeText(response.data.invite_url).catch(() => {});
      }
      
      fetchInvitations();
    } catch (err) {
      toast.error(normalizeError(err.response?.data) || "Failed to resend invitation");
    } finally {
      setActionLoading(prev => ({ ...prev, [inviteId]: null }));
    }
  }, [getAuthHeaders, fetchInvitations]);

  // Revoke invitation
  const handleRevoke = useCallback(async (inviteId) => {
    if (!window.confirm("Are you sure you want to revoke this invitation?")) return;
    
    setActionLoading(prev => ({ ...prev, [inviteId]: "revoke" }));
    try {
      await axios.delete(
        `${API}/admin/invitations/${inviteId}`,
        { withCredentials: true }
      );
      toast.success("Invitation revoked");
      fetchInvitations();
    } catch (err) {
      toast.error(normalizeError(err.response?.data) || "Failed to revoke invitation");
    } finally {
      setActionLoading(prev => ({ ...prev, [inviteId]: null }));
    }
  }, [getAuthHeaders, fetchInvitations]);

  // Permanent delete invitation (for REVOKED/EXPIRED only)
  const handlePermanentDelete = useCallback(async (inviteId, email) => {
    if (!window.confirm(`Permanently delete the invitation for ${email}? This cannot be undone.`)) return;
    
    setActionLoading(prev => ({ ...prev, [inviteId]: "delete" }));
    try {
      await axios.delete(
        `${API}/admin/invitations/${inviteId}/permanent`,
        { withCredentials: true }
      );
      toast.success("Invitation permanently deleted");
      fetchInvitations();
    } catch (err) {
      toast.error(normalizeError(err.response?.data) || "Failed to delete invitation");
    } finally {
      setActionLoading(prev => ({ ...prev, [inviteId]: null }));
    }
  }, [getAuthHeaders, fetchInvitations]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" />
            Invitations
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage user invitations. New users must be invited to access the system.
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchInvitations}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          
          <Button size="sm" onClick={openModal} data-testid="create-invite-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Invitation
          </Button>
        </div>
      </div>

      {/* Create Modal - Using simple native modal to avoid Radix ref issues */}
      <SimpleModal
        open={modalOpen}
        onClose={closeModal}
        title="Invite New User"
        description="Send an invitation to join Kanban Inventory"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="invite-email">Email Address</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="user@company.com"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              required
              autoFocus
              data-testid="invite-email-input"
            />
          </div>
          
          {/* Roles - Using native checkboxes */}
          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="grid gap-2">
              {ROLES.map((role) => (
                <label
                  key={role.value}
                  className="flex items-center space-x-3 p-2 border border-border rounded-sm hover:bg-muted/50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formRoles.includes(role.value)}
                    onChange={() => toggleRole(role.value)}
                    className="h-4 w-4 rounded border-primary text-primary focus:ring-primary"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium capitalize">{role.label}</p>
                    <p className="text-xs text-muted-foreground">{role.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          
          {/* Expires - Using native select */}
          <div className="space-y-2">
            <Label htmlFor="invite-expires">Expires In</Label>
            <select
              id="invite-expires"
              value={formExpires}
              onChange={(e) => setFormExpires(e.target.value)}
              className="flex h-11 w-full items-center justify-between rounded-sm border-2 border-muted-foreground/20 bg-muted/50 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            >
              <option value="1">1 day</option>
              <option value="3">3 days</option>
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
            </select>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={creating} data-testid="send-invite-btn">
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </div>
        </form>
      </SimpleModal>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="text-sm">Filter by Status:</Label>
            <div className="flex gap-2">
              {["all", "PENDING", "ACCEPTED", "EXPIRED", "REVOKED"].map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  className="capitalize"
                >
                  {status === "all" ? "All" : status.toLowerCase()}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invitations Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            Invitations ({invitations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No invitations found</p>
              <p className="text-sm">Create an invitation to add new users</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Email Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((invite) => {
                  const StatusIcon = STATUS_ICONS[invite.status];
                  const expired = invite.status === "PENDING" && new Date(invite.expires_at) < new Date();
                  const effectiveStatus = expired ? "EXPIRED" : invite.status;
                  const emailStatus = invite.email_status || "not_sent";
                  const canDelete = effectiveStatus === "REVOKED" || effectiveStatus === "EXPIRED";
                  
                  return (
                    <TableRow key={invite.id}>
                      <TableCell className="font-medium">{invite.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {invite.roles.map((role) => (
                            <Badge key={role} variant="secondary" className="text-xs capitalize">
                              {role}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[effectiveStatus]} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {effectiveStatus.toLowerCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={`${EMAIL_STATUS_COLORS[emailStatus]} border`}
                          title={invite.email_error || undefined}
                        >
                          {EMAIL_STATUS_LABELS[emailStatus] || "Unknown"}
                          {emailStatus === "sent" && invite.send_count > 1 && (
                            <span className="ml-1">({invite.send_count}x)</span>
                          )}
                        </Badge>
                        {emailStatus === "failed" && invite.email_error && (
                          <p className="text-xs text-red-400 mt-1 max-w-[200px] truncate" title={invite.email_error}>
                            {invite.email_error}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(invite.created_at)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(invite.expires_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Resend button for PENDING invitations */}
                          {invite.status === "PENDING" && !expired && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResend(invite.id)}
                                disabled={actionLoading[invite.id] === "resend"}
                                title="Resend invitation"
                              >
                                {actionLoading[invite.id] === "resend" ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevoke(invite.id)}
                                disabled={actionLoading[invite.id] === "revoke"}
                                className="text-destructive hover:text-destructive"
                                title="Revoke invitation"
                              >
                                {actionLoading[invite.id] === "revoke" ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </>
                          )}
                          
                          {/* Delete button for REVOKED/EXPIRED invitations */}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePermanentDelete(invite.id, invite.email)}
                              disabled={actionLoading[invite.id] === "delete"}
                              className="text-destructive hover:text-destructive"
                              title="Permanently delete"
                              data-testid={`delete-invite-${invite.id}`}
                            >
                              {actionLoading[invite.id] === "delete" ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
